import React, { useState, useCallback, useEffect } from "react";
import Layout from "../../../../../components/Layout";
import Container from "../../../../../components/Container";
import Flex from "../../../../../components/Flex";
import Breadcrumbs from "../../../../../components/Breadcrumbs";
import { Link, useHistory, useParams } from "react-router-dom";
import Content from "../../../../../components/Content";
import Button from "../../../../../components/Button";
import ButtonList from "../../../../../components/ButtonList";
import LinkButton from "../../../../../components/LinkButton";
import DataTable from "../../../../../components/DataTable";
import { useAuthState } from "../../../../../components/AuthProvider";
import { fetchAggregateCountData } from "../../../../../utils/fetchData";
import { fetchJsonWithRetry } from "../../../../../utils/batchFetch";

import styles from "./ViewAccount.module.scss";
import FlickerText from "../../../../../components/FlickerText";
import Spinner from "../../../../../components/Loader/Spinner";

const ViewAccount = () => {
  const moment = require("moment-timezone");
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const orgApiUrl = `${clientConfigApiDomain}/organizations`;
  const clientApiUrl = `${clientConfigApiDomain}/rpc/clients`;
  const clientSubscriptionsApiUrl = `${clientConfigApiDomain}/client_subscriptions`;
  const sourceApiUrl = `${clientConfigApiDomain}/sources`;
  const subscriptionApiUrl = `${clientConfigApiDomain}/subscriptions`;
  const matchModelApiUrl = `${clientConfigApiDomain}/match_models`;

  const { id, clientId } = useParams();

  const history = useHistory();
  const { user } = useAuthState();

  const [aggregateCountData, setAggregateCountData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [orgData, setOrgData] = useState([]);
  const [clientData, setClientData] = useState([]);
  const [clientSubscriptionsData, setClientSubscriptionsData] = useState([]);
  const [clientSourcesData, setClientSourcesData] = useState([]);

  // Batched full fetch of all sources. Introduced as part of DE1180408 to reduce incidence of 504 errors when an org has many sources.
  const SOURCES_THRESHOLD = 500; // if total number of sources is greater than this, use batching
  const SOURCES_BATCH_SIZE = 500; // per request

  const loadAllSources = async clientId => {
    // Part 1: lightweight count request (limit=1 to minimize payload)
    const baseQuery = `client_subscription__client_id=${clientId}`;
    const countUrl = `${sourceApiUrl}/?${baseQuery}&limit=1&offset=0`;
    const countResp = await fetchJsonWithRetry(countUrl);
    const countPayload = await countResp.json();
    const total =
      typeof countPayload.count === "number"
        ? countPayload.count
        : countPayload.results?.length || 0;

    if (total === 0) {
      setClientSourcesData([]);
      return;
    }

    // If small enough just pull everything in one shot
    if (total <= SOURCES_THRESHOLD) {
      const singleUrl = `${sourceApiUrl}/?${baseQuery}&limit=${total}&offset=0`;
      const allResp = total === 1 ? null : await fetchJsonWithRetry(singleUrl);
      const allPayload = total === 1 ? countPayload : await allResp.json();
      setClientSourcesData(allPayload.results || []);
      return;
    }

    // Part 2: batched requests
    const batches = [];
    for (let offset = 0; offset < total; offset += SOURCES_BATCH_SIZE) {
      const limit = Math.min(SOURCES_BATCH_SIZE, total - offset);
      const url = `${sourceApiUrl}/?${baseQuery}&limit=${limit}&offset=${offset}`;
      batches.push(url);
    }

    const aggregated = [];
    // Fetch sequentially to reduce server load (change to Promise.all in chunks if desired)
    for (let i = 0; i < batches.length; i++) {
      const resp = await fetchJsonWithRetry(batches[i]);
      const payload = await resp.json();
      if (payload.results && payload.results.length) {
        aggregated.push(...payload.results);
      }
    }

    // Part 3: final state update
    setClientSourcesData(aggregated);
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    window.scrollTo(0, 0);
    const response = await fetch(`${clientApiUrl}/?id=${clientId}`);
    const data = await response.json();
    setClientData(data.results[0]);

    const orgResponse = await fetch(`${orgApiUrl}/${id}/`);
    const orgData = await orgResponse.json();
    setOrgData(orgData);

    // get all subscription types
    const subscriptionListResponse = await fetch(`${subscriptionApiUrl}/`);
    const subscriptionListData = await subscriptionListResponse.json();
    // get all match models
    const matchModelListResponse = await fetch(`${matchModelApiUrl}/`);
    const matchModelListData = await matchModelListResponse.json();

    const clientSubscriptionsResponse = await fetch(
      `${clientSubscriptionsApiUrl}/?client__id=${clientId}`
    );
    if (clientSubscriptionsResponse.status === 200) {
      const clientSubData = await clientSubscriptionsResponse.json();
      clientSubData.results.forEach(clientSub => {
        const subsData = subscriptionListData.results.filter(
          subData => subData.url === clientSub.subscription.url
        );
        clientSub.subscription_name = subsData[0].name;
        const mmsData = matchModelListData.results.filter(
          mmData => mmData.url === clientSub.match_model
        );
        clientSub.match_model_name = mmsData[0].name;
      });
      setClientSubscriptionsData(clientSubData.results);
    }

    try {
      await loadAllSources(clientId); // wait to set the state until all batch lists are assembled
    } catch (e) {
      console.error(
        "Failed to load sources fully, falling back to full fetching:",
        e
      );
      // fallback minimal attempt
      try {
        const fallback = await fetch(
          `${sourceApiUrl}/?client_subscription__client_id=${clientId}`
        );
        if (fallback.ok) {
          const minimal = await fallback.json();
          setClientSourcesData(minimal.results || []);
        }
      } catch (_) {
        console.error("Fallback fetching failed:", _);
      }
    }

    const fetchAggregateCountDataResponseData = await fetchAggregateCountData(
      clientId
    );
    if (fetchAggregateCountDataResponseData.results) {
      setAggregateCountData(fetchAggregateCountDataResponseData.results);
    }

    setIsLoading(false);
    setHasLoaded(true);
  }, [
    clientApiUrl,
    clientId,
    clientSubscriptionsApiUrl,
    subscriptionApiUrl,
    matchModelApiUrl,
    id,
    orgApiUrl,
    sourceApiUrl
  ]);

  useEffect(() => {
    if (!hasLoaded) {
      fetchData();
    }
  }, [fetchData, hasLoaded]);

  const clientSubscriptionClickableCell = ({
    cell: {
      value,
      row: { original: selectedSubscription }
    }
  }) => {
    const onClick = () => {
      history.push(
        `/admin/organizations/${id}/accounts/${clientId}/subscriptions/${selectedSubscription.id}`
      );
    };
    return (
      <span className={styles.cell} onClick={onClick}>
        {value}
      </span>
    );
  };
  const clientSubscriptionActionCell = ({
    cell: {
      value,
      row: { original: selectedSubscription }
    }
  }) => {
    const onDetailsClick = () => {
      history.push(
        `/admin/organizations/${id}/accounts/${clientId}/subscriptions/${selectedSubscription.id}`
      );
    };
    const onEditClick = () => {
      history.push(
        `/admin/organizations/${id}/accounts/${clientId}/subscriptions/${selectedSubscription.id}/edit`
      );
    };

    return (
      <ButtonList className={styles.icons}>
        <LinkButton icon="view" onClick={onDetailsClick}>
          Details
        </LinkButton>
        {(user.role === "Global Admin" || user.orgIds.includes(orgData.id)) && (
          <LinkButton icon="edit" onClick={onEditClick}>
            Edit
          </LinkButton>
        )}
      </ButtonList>
    );
  };
  const clientSubscriptionColumns = [
    {
      Header: "Subscription",
      accessor: "subscription_name",
      Cell: clientSubscriptionClickableCell
    },
    {
      Header: "Match Model",
      accessor: "match_model_name"
    },
    {
      Header: "S3 Bucket",
      accessor: "s3_bucket_name"
    },
    {
      Header: "Sink Kafka Topic",
      accessor: "sink_kafka_topic_name"
    },
    {
      Header: "Source Kafka Topic",
      accessor: "source_kafka_topic_name"
    },
    {
      Header: "Entity Changes Kafka Topic",
      accessor: "entity_changes_kafka_topic_name"
    },
    {
      Header: "",
      disableFilters: true,
      disableSortBy: true,
      accessor: "editDelete",
      Cell: clientSubscriptionActionCell
    }
  ];

  const sourceClickableCell = ({
    cell: {
      value,
      row: { original: selectedSource }
    }
  }) => {
    const onClick = () => {
      history.push(
        `/admin/organizations/${id}/accounts/${clientId}/sources/${selectedSource.id}`
      );
    };
    return (
      <span className={styles.cell} onClick={onClick}>
        {value}
      </span>
    );
  };

  const trusterMasterIdentifierCell = ({ cell: { value } }) => {
    return <span>{value.toString()}</span>;
  };

  const renderRestrictionTypeCell = value => {
    const restrictionType = {
      noRestriction: "No Restriction",
      policyRestricted: "Policy Restricted",
      sourceRestricted: "Source Restricted"
    };
    return restrictionType[value] || "No Restriction";
  };

  const actionCell = ({
    cell: {
      value,
      row: { original: selectedSource }
    }
  }) => {
    const onDetailsClick = () => {
      history.push(
        `/admin/organizations/${id}/accounts/${clientId}/sources/${selectedSource.id}`
      );
    };
    const onEditClick = () => {
      history.push(
        `/admin/organizations/${id}/accounts/${clientId}/sources/${selectedSource.id}/edit`
      );
    };

    return (
      <ButtonList className={styles.icons}>
        <LinkButton icon="view" onClick={onDetailsClick}>
          Details
        </LinkButton>
        {(user.role === "Global Admin" || user.orgIds.includes(orgData.id)) && (
          <LinkButton icon="edit" onClick={onEditClick}>
            Edit
          </LinkButton>
        )}
      </ButtonList>
    );
  };

  const columns = [
    {
      Header: "Source Name",
      accessor: "name",
      Cell: sourceClickableCell
    },
    {
      Header: "Source OID",
      accessor: "oid"
    },
    {
      Header: "Source Code",
      accessor: "code"
    },
    {
      Header: "Assigning Authority OID",
      accessor: "assigning_authority_oid"
    },
    {
      Header: "Default Trust Score",
      accessor: "trust_score"
    },
    {
      Header: "Restriction Type",
      accessor: "restriction",
      Cell: ({ cell: { value } }) => renderRestrictionTypeCell(value)
    },
    {
      Header: "Trusted Master Identifier",
      accessor: "trusted_master_identifier",
      Cell: trusterMasterIdentifierCell,
      disableFilters: true,
      disableSortBy: true
    },
    {
      Header: "",
      disableFilters: true,
      disableSortBy: true,
      accessor: "editDelete",
      Cell: actionCell
    }
  ];

  let loadedBreadcrumbs = "";
  if (isLoading) {
    loadedBreadcrumbs = <Spinner />;
  } else if (hasLoaded) {
    loadedBreadcrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item
          render={() => <Link to="/admin/organizations">Organization</Link>}
        />
        <Breadcrumbs.Item
          render={() => (
            <Link to={`/admin/organizations/${id}/`}>
              Organization: {orgData.name}
            </Link>
          )}
        />
        <Breadcrumbs.Item
          last
          render={() => `Account: ${clientData.name}`}
        ></Breadcrumbs.Item>
      </Breadcrumbs>
    );
  }

  const isEditVisible = orgData => {
    if (orgData.length !== 0)
      return (
        user.role === "Global Admin" ||
        user.orgIds.includes(orgData.organization.id)
      );
  };

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item>{loadedBreadcrumbs}</Flex.Item>
          <Flex.Item span={12}>
            <Content>
              {isLoading ? (
                <Spinner />
              ) : hasLoaded ? (
                <h1>Account: {clientData.name}</h1>
              ) : null}
            </Content>
            <div className={styles.base}>
              <div className={styles.orgSettings}>
                <h3>Account Details</h3>
                <div>
                  {isEditVisible(clientData) && (
                    <ButtonList>
                      <Button
                        primary
                        onClick={() => history.push(`${clientId}/edit`)}
                      >
                        Edit
                      </Button>
                    </ButtonList>
                  )}
                </div>
              </div>
              {isLoading ? (
                <Spinner />
              ) : hasLoaded ? (
                <div className={styles.orgDetails}>
                  <div>
                    <h4>Account ID</h4>
                    <span>{clientData.id}</span>
                  </div>
                  <div>
                    <h4>Name</h4>
                    <span>{clientData.name}</span>
                  </div>
                  <div>
                    <h4>OID</h4>
                    <span>{clientData.oid}</span>
                  </div>
                  <div>
                    <h4>Short Code</h4>
                    <span>{clientData.short_code}</span>
                  </div>
                  <div>
                    <h4>code</h4>
                    <span>{clientData.code}</span>
                  </div>
                  <div>
                    <h4>Maintenance Mode</h4>
                    {clientData.maintenance_mode ? (
                      <FlickerText text="Enabled" />
                    ) : (
                      <span>Disabled</span>
                    )}
                  </div>
                  <div>
                    <h4>Created By</h4>
                    <span> {clientData.created_by} </span>
                  </div>
                  <div>
                    <h4>Date Created</h4>
                    <span>
                      {moment(clientData.created_at)
                        .tz(user.preferences.timeZone)
                        .format("L, LTS z")}
                    </span>
                  </div>
                  <div>
                    <h4>Updated By</h4>
                    <span>{clientData.updated_by}</span>
                  </div>
                  <div>
                    <h4>Date Updated</h4>
                    <span>
                      {moment(clientData.updated_at)
                        .tz(user.preferences.timeZone)
                        .format("L, LTS z")}
                    </span>
                  </div>
                  <div>
                    <h4>Total number of Active lives</h4>
                    <span>
                      {aggregateCountData.total_added &&
                      aggregateCountData.total_removed
                        ? (
                            aggregateCountData.total_added -
                            aggregateCountData.total_removed
                          ).toLocaleString()
                        : `NA`}
                    </span>
                  </div>
                </div>
              ) : null}
              <div className={styles.orgAccounts}>
                <h3>Account Subscriptions</h3>
                <ButtonList spacing={2} style={{ margin: "1rem 0" }}>
                  <LinkButton icon="add" disabled={true}>
                    Add Subscription
                  </LinkButton>
                </ButtonList>
                <div className={styles.table}>
                  {isLoading ? (
                    <Spinner />
                  ) : hasLoaded ? (
                    <DataTable
                      columns={clientSubscriptionColumns}
                      data={clientSubscriptionsData}
                      hasSorting
                    />
                  ) : null}
                </div>
              </div>
              <div className={styles.orgAccounts}>
                <h3>Account Sources</h3>
                <ButtonList spacing={2} style={{ margin: "1rem 0" }}>
                  <LinkButton
                    icon="add"
                    disabled={!isEditVisible(clientData)}
                    onClick={() =>
                      history.push(`${history.location.pathname}/sources/add`)
                    }
                  >
                    Add Source
                  </LinkButton>
                </ButtonList>
                <div className={styles.table}>
                  {isLoading ? (
                    <Spinner />
                  ) : hasLoaded ? (
                    <DataTable
                      columns={columns}
                      data={clientSourcesData}
                      hasSorting
                      hasPagination
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </Flex.Item>
        </Flex>
      </Container>
    </Layout>
  );
};

export default ViewAccount;
